/**
 * MagicLoginPage.tsx
 *
 * Handles the magic link landing page.
 * URL format: /magic-login?token=<48-byte-hex>
 *
 * On mount: reads the token from the URL, calls GET /magic-link/verify,
 * and either logs the user in or shows an error.
 *
 * In App.tsx this should be rendered when the URL path is /magic-login.
 * Since Coast has no React Router, we detect the path in App.tsx and
 * render this component instead of Login or Dashboard.
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = 'https://coast-terminal-production.up.railway.app';

interface Props {
  onLogin: (token: string, name?: string, role?: string) => void;
}

const MagicLoginPage: React.FC<Props> = ({ onLogin }) => {
  const [status, setStatus] = useState<'loading' | 'mfa' | 'error'>('loading');
  const [error, setError] = useState('');
  const [mfaUsername, setMfaUsername] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState('');

  const gold = '#C9A96E';
  const bg = '#080d18';
  const surface = '#0e1420';
  const surfaceHigh = '#1a2438';
  const border = '#1e2a3d';
  const borderLight = '#243448';
  const textPrimary = '#E8DCC8';
  const textSecondary = '#7A8CA0';
  const textMuted = '#3A4A5C';
  const red = '#CC2200';
  const green = '#1A7A4A';
  const serifFont = "'Cormorant Garamond', Georgia, serif";

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (!token) {
      setStatus('error');
      setError('No token found in the URL. Please request a new login link.');
      return;
    }

    axios.get(`${API}/magic-link/verify?token=${token}`)
      .then(res => {
        if (res.data.mfa_required) {
          setMfaUsername(res.data.username);
          setStatus('mfa');
        } else {
          onLogin(res.data.access_token, res.data.name, res.data.role);
        }
      })
      .catch(err => {
        setStatus('error');
        setError(err?.response?.data?.detail || 'This link is invalid or has expired. Please request a new one.');
      });
  }, []); // eslint-disable-line

  const handleMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mfaCode.length !== 6) return;
    setMfaLoading(true);
    setMfaError('');
    try {
      const res = await axios.post(`${API}/mfa/confirm`, { username: mfaUsername, code: mfaCode });
      onLogin(res.data.access_token, res.data.name, res.data.role);
    } catch (err: any) {
      setMfaError(err?.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setMfaLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', -apple-system, sans-serif" }}>
      <div style={{ width: '100%', maxWidth: '420px', padding: '40px 32px', backgroundColor: surface, border: `1px solid ${border}` }}>

        {/* Logo */}
        <div style={{ fontSize: '22px', fontWeight: 800, letterSpacing: '6px', fontFamily: serifFont, color: gold, marginBottom: '28px', textAlign: 'center' }}>
          COAST
        </div>

        {/* Loading */}
        {status === 'loading' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: textSecondary, marginBottom: '8px', fontFamily: serifFont }}>Signing you in...</div>
            <div style={{ fontSize: '11px', color: textMuted, letterSpacing: '1.5px', textTransform: 'uppercase' }}>Verifying your link</div>
          </div>
        )}

        {/* Error */}
        {status === 'error' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '16px', color: textPrimary, fontFamily: serifFont, marginBottom: '12px', fontWeight: 600 }}>Link Expired</div>
            <div style={{ fontSize: '13px', color: textSecondary, lineHeight: 1.7, marginBottom: '24px', fontFamily: serifFont }}>{error}</div>
            <a href="/" style={{ display: 'inline-block', padding: '11px 28px', backgroundColor: gold, color: bg, fontWeight: 800, fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', textDecoration: 'none' }}>
              Back to Login
            </a>
          </div>
        )}

        {/* MFA */}
        {status === 'mfa' && (
          <form onSubmit={handleMfa}>
            <div style={{ fontSize: '16px', color: textPrimary, fontFamily: serifFont, fontWeight: 600, marginBottom: '8px', textAlign: 'center' }}>
              Two-Factor Authentication
            </div>
            <div style={{ fontSize: '13px', color: textSecondary, lineHeight: 1.7, marginBottom: '24px', textAlign: 'center', fontFamily: serifFont }}>
              Enter the 6-digit code from your authenticator app.
            </div>
            <input
              type="text"
              placeholder="000000"
              value={mfaCode}
              onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              maxLength={6}
              autoFocus
              style={{ width: '100%', boxSizing: 'border-box', backgroundColor: surfaceHigh, border: `1px solid ${borderLight}`, padding: '14px', color: textPrimary, fontSize: '24px', outline: 'none', fontFamily: 'monospace', letterSpacing: '10px', textAlign: 'center', marginBottom: '14px' }}
            />
            {mfaError && (
              <div style={{ fontSize: '12px', color: red, marginBottom: '12px', textAlign: 'center' }}>{mfaError}</div>
            )}
            <button type="submit" disabled={mfaLoading || mfaCode.length !== 6} style={{ width: '100%', padding: '14px', backgroundColor: mfaCode.length === 6 ? gold : surfaceHigh, color: mfaCode.length === 6 ? bg : textMuted, border: 'none', fontWeight: 800, fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', cursor: mfaCode.length === 6 ? 'pointer' : 'default', fontFamily: 'inherit' }}>
              {mfaLoading ? 'Verifying...' : 'Verify'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default MagicLoginPage;